import { Fragment, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, Modal, useToast, useConfirm } from "@/components/ui";
import { Field, TextInput } from "@/components/fields";

export function AdminRoles() {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, node } = useConfirm();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});

  const { data: rolesData, isLoading } = useQuery({ queryKey: ["roles"], queryFn: () => api.get<{ roles: any[] }>("/roles") });
  const { data: permData } = useQuery({ queryKey: ["permissions"], queryFn: () => api.get<{ permissions: any[] }>("/meta/permissions") });
  const roles = rolesData?.roles ?? [];
  const perms = permData?.permissions ?? [];
  const categories = useMemo(() => [...new Set(perms.map((p) => p.category))], [perms]);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["roles"] }); setDraft({}); };

  const savePerms = useMutation({
    mutationFn: ({ id, permissions }: any) => api.put(`/roles/${id}`, { permissions }),
    onSuccess: () => { invalidate(); toast.push("success", "Permissions updated"); },
    onError: (e: any) => toast.push("error", e?.message || "Failed"),
  });
  const reset = useMutation({ mutationFn: (id: string) => api.post(`/roles/${id}/reset`, {}), onSuccess: () => { invalidate(); toast.push("success", "Reset to defaults"); } });
  const del = useMutation({ mutationFn: (id: string) => api.del(`/roles/${id}`), onSuccess: () => { invalidate(); toast.push("success", "Role deleted"); } });

  const effective = (roleId: string): Set<string> => draft[roleId] ?? new Set(roles.find((r) => r.id === roleId)?.permissions ?? []);
  const toggle = (roleId: string, key: string) => {
    setDraft((d) => {
      const cur = new Set(d[roleId] ?? roles.find((r) => r.id === roleId)?.permissions ?? []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...d, [roleId]: cur };
    });
  };

  if (isLoading) return <PageLoader />;

  return (
    <>
      <PageHeader title="Roles & Permissions" subtitle="Bundles of capabilities you can assign to users. Super Admin always has everything."
        actions={<button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> New role</button>} />

      <div className="grid-scroll overflow-x-auto rounded-xl border border-line">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr>
              <th className="th sticky left-0 z-10 bg-surface-2 min-w-[280px]">Permission</th>
              {roles.map((r) => (
                <th key={r.id} className="th text-center">
                  <div className="font-semibold">{r.name}</div>
                  <div className="font-normal text-ink-faint">{r.userCount} user{r.userCount === 1 ? "" : "s"}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <Fragment key={cat}>
                <tr className="bg-surface-2">
                  <td className="td font-bold uppercase tracking-wide text-xs" colSpan={roles.length + 1}>{cat}</td>
                </tr>
                {perms.filter((p) => p.category === cat).map((p) => (
                  <tr key={p.key} className="border-b border-line">
                    <td className="td sticky left-0 z-10 bg-surface">
                      <span className="font-medium">{p.key}</span>
                      <span className="block text-xs text-ink-faint">{p.description}</span>
                    </td>
                    {roles.map((r) => {
                      const isSuper = r.key === "super_admin";
                      const has = isSuper || effective(r.id).has(p.key);
                      return (
                        <td key={r.id} className="td text-center">
                          <input type="checkbox" disabled={isSuper} checked={has}
                            onChange={() => toggle(r.id, p.key)} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="td sticky left-0 bg-surface" />
              {roles.map((r) => (
                <td key={r.id} className="td text-center">
                  {r.key !== "super_admin" && (
                    <div className="flex flex-col items-center gap-1">
                      {draft[r.id] && (
                        <button className="btn-primary !px-2 !py-1 text-xs" onClick={() => savePerms.mutate({ id: r.id, permissions: [...effective(r.id)] })}>
                          <Save size={12} /> Save
                        </button>
                      )}
                      <div className="flex gap-1">
                        <button className="btn-ghost !p-1 text-xs" title="Reset to defaults" onClick={() => reset.mutate(r.id)}><RotateCcw size={12} /></button>
                        {!r.isSystem && (
                          <button className="btn-ghost !p-1 text-xs text-st-delayed" title="Delete role"
                            onClick={async () => { if (await confirm("Delete role", `Delete "${r.name}"?`, true)) del.mutate(r.id); }}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {creating && <NewRoleModal perms={perms} onClose={() => { setCreating(false); invalidate(); }} />}
      {node}
    </>
  );
}

function NewRoleModal({ perms, onClose }: { perms: any[]; onClose: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ name: "", description: "" });
  const [sel, setSel] = useState<Set<string>>(new Set());
  const create = useMutation({
    mutationFn: () => api.post("/roles", { ...f, permissions: [...sel] }),
    onSuccess: () => { toast.push("success", "Role created"); onClose(); },
    onError: (e: any) => toast.push("error", e?.message || "Failed"),
  });
  return (
    <Modal open onClose={onClose} title="New role" wide
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!f.name || create.isPending} onClick={() => create.mutate()}>Create</button>
      </>}>
      <div className="space-y-3">
        <Field label="Role name" required><TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Description"><TextInput value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <div>
          <p className="label">Permissions</p>
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-line p-2">
            {perms.map((p) => (
              <label key={p.key} className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" checked={sel.has(p.key)}
                  onChange={() => setSel((s) => { const n = new Set(s); n.has(p.key) ? n.delete(p.key) : n.add(p.key); return n; })} />
                <span>{p.key} <span className="block text-xs text-ink-faint">{p.description}</span></span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
